import type { ReactNode } from "react";
import type { TFunction } from "i18next";
import {
  ChartArea,
  ChartColumnStacked,
  ChartNetwork,
  Layers,
  SquareChartGantt,
} from "lucide-react";
import { hasSocialListeningAccess, hasUserManagementAccess } from "src/utils/sectionAccess";
import { DASHBOARD_ROUTE_CONFIG } from "./DashboardRoutes";

export interface SidebarItem {
  name: string;
  route?: string;
  children?: SidebarItem[];
}

export interface SidebarSectionConfig {
  key: string;
  label: string;
  icon: ReactNode;
  route?: string;
  items?: SidebarItem[];
}

export const HELP_SUPPORT_ROUTE = "/account/help-and-support";

const WHITE_ICON_STYLE = { color: "#ffffff", stroke: "#ffffff" };

const sidebarImage = (src: string, alt: string) => (
  <img src={src} alt={alt} className="sidebar-icon-img" />
);

export const SIDEBAR_ROUTE_ICONS: Record<string, ReactNode> = {
  [DASHBOARD_ROUTE_CONFIG[280].route]: sidebarImage("/Icons/salesIcon.svg", "Sales"),
  [DASHBOARD_ROUTE_CONFIG[293].route]: <ChartNetwork className="sidebar-icon-img" />,
  [DASHBOARD_ROUTE_CONFIG[301].route]: sidebarImage("/Icons/organicmap-grey.svg", "Organic"),
  [DASHBOARD_ROUTE_CONFIG[296].route]: <ChartColumnStacked className="sidebar-icon-img" />,
  [DASHBOARD_ROUTE_CONFIG[302].route]: sidebarImage("/Icons/paid.svg", "Paid"),
  [DASHBOARD_ROUTE_CONFIG[303].route]: <ChartArea className="sidebar-icon-img" />,
  [DASHBOARD_ROUTE_CONFIG[304].route]: (
    <SquareChartGantt className="sidebar-icon-img" style={WHITE_ICON_STYLE} />
  ),
  [DASHBOARD_ROUTE_CONFIG[3].route]: <Layers className="sidebar-icon-img" style={WHITE_ICON_STYLE} />,
  "/account/engagement/email-analytics-overview": sidebarImage("/Icons/Overview.svg", "Overview"),
  "/account/engagement/campaign-analytics": sidebarImage("/Icons/campaigns.svg", "Campaigns"),
  "/account/engagement/audience": sidebarImage("/Icons/audience.svg", "Audience"),
  "/account/my-account/account-settings": sidebarImage("/Icons/Settings.svg", "Settings"),
  "/account/my-account/notification-preferences": sidebarImage("/Icons/Preference.svg", "Preference"),
  "/account/my-account/all-notifications": sidebarImage("/Icons/notification.svg", "Notifications"),
};

const filterSidebarSections = (sections: SidebarSectionConfig[]) =>
  sections.filter(
    section =>
      !(section.key === "SocialListingAgent" && !hasSocialListeningAccess()) &&
      !(section.key === "userManagement" && !hasUserManagementAccess())
  );

export const buildSidebarSections = (
  t: TFunction,
  dashboardItems: SidebarItem[]
): SidebarSectionConfig[] =>
  filterSidebarSections([
    {
      key: "dashboards",
      icon: <img src="/Icons/DashboardIcon.svg" className="sidebar-icon-img" />,
      label: t("sidebar.sections.dashboards"),
      items: dashboardItems,
    },
    {
      key: "dataSources",
      icon: sidebarImage("/Icons/SourcesIcon.svg", "Sources"),
      label: t("sidebar.sections.connectedDataSources"),
      route: "/account/connect-sources",
      items: [],
    },
    {
      key: "engagements",
      icon: sidebarImage("/Icons/EnagagementIcon.svg", "Engagement"),
      label: t("sidebar.sections.customerMarketEngagements"),
      items: [
        {
          name: t("sidebar.items.overview"),
          route: "/account/engagement/email-analytics-overview",
        },
        {
          name: t("sidebar.items.campaigns"),
          route: "/account/engagement/campaign-analytics",
        },
        {
          name: t("sidebar.items.audience"),
          route: "/account/engagement/audience",
        },
      ],
    },
    {
      key: "SocialListingAgent",
      icon: sidebarImage("/Icons/mail-list.svg", "socialListing"),
      label: t("sidebar.sections.socialListeningAgent"),
      route: "/account/social-listing-agent",
      items: [],
    },
    {
      key: "aiChatbot",
      icon: <img src="/Icons/chatbot-logo.png" alt="aichatbot" className="sidebar-img" />,
      label: t("sidebar.sections.askDataTram"),
      route: "/account/ai-chat",
      items: [],
    },
    {
      key: "userManagement",
      icon: sidebarImage("/Icons/UserIcon.svg", "UserManagement"),
      label: t("sidebar.sections.userManagement"),
      route: "/account/user-management",
      items: [],
    },
    {
      key: "settings",
      icon: sidebarImage("/Icons/AccountIcon.svg", "MyAccount"),
      label: t("sidebar.sections.accountSettings"),
      items: [
        {
          name: t("sidebar.items.myAccount"),
          route: "/account/my-account/account-settings",
        },
        {
          name: t("sidebar.items.notificationPreferences"),
          route: "/account/my-account/notification-preferences",
        },
        {
          name: t("sidebar.items.allNotifications"),
          route: "/account/my-account/all-notifications",
        },
      ],
    },
  ]);
